import { FC, useLayoutEffect, useRef, useState } from 'react';
import { ActionTypes, IPrompt, ISource } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';

import { PromptView } from './Message/PromptView';
import { DateSeparator } from './DateSeparator';
import './PromptList.scss';

export interface PromptListProps {
    prompts: IPrompt[];
    conversationTitle?: string;
    onUpdate: (actionType: ActionTypes, prompts: IPrompt[], updatedPromptIndex: number) => void;
    onUpdateAnsweredQuery: (updatedPromptIndex: number, newQuery: string) => void;
    onSourceClick: (source: ISource) => void;
    onEditingChange?: (isEditing: boolean) => void;
}

// renders the list of messages
export const PromptList: FC<PromptListProps> = (props: PromptListProps) => {
    const { prompts, onUpdate, onUpdateAnsweredQuery, onSourceClick, onEditingChange } = props;
    const promptList = useRef<HTMLDivElement | null>(null);
    // sentinel at the end of the list to reliably scroll into view
    const endRef = useRef<HTMLDivElement | null>(null);
    // hide list until we've scrolled to bottom to avoid visible "jump" effect
    const [isReady, setIsReady] = useState<boolean>(false);
    
    // Utility: find the nearest scrollable ancestor of an element
    const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
        if (!element) return null;
        let parent: HTMLElement | null = element.parentElement;
        const overflowRegex = /(auto|scroll)/;

        while (parent) {
            const style = window.getComputedStyle(parent);
            const overflowY = style.overflowY;
            if (overflowRegex.test(overflowY) && parent.scrollHeight > parent.clientHeight) {
                return parent;
            }
            parent = parent.parentElement;
        }
        // fallback to document.scrollingElement
        return document.scrollingElement as HTMLElement | null;
    };

    useLayoutEffect(() => {
        let mounted = true;
        setIsReady(false);

        const scrollToBottom = () => {
            if (!mounted) return;
            if (endRef?.current) {
                try { endRef.current.scrollIntoView({ behavior: 'auto', block: 'end' }); return; } catch {}
            }
            const sp = getScrollParent(promptList.current);
            if (sp) try { sp.scrollTop = sp.scrollHeight; } catch {}
        };

        scrollToBottom();
        const t1 = window.setTimeout(scrollToBottom, 100);
        // default show after short wait; ResizeObserver can make it earlier
        const showT = window.setTimeout(() => { if (mounted) setIsReady(true); }, 150);

        let ro: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined' && promptList.current) {
            try {
                ro = new ResizeObserver(() => { if (mounted) { scrollToBottom(); setIsReady(true); ro?.disconnect(); } });
                ro.observe(promptList.current);
            } catch {}
        }

        return () => {
            mounted = false;
            clearTimeout(t1);
            clearTimeout(showT);
            if (ro) try { ro.disconnect(); } catch {}
        };
    }, [prompts]);

    // Helper function to check if two dates are on different days
    const isSameDay = (date1: Date, date2: Date): boolean => {
        return date1.getDate() === date2.getDate() &&
               date1.getMonth() === date2.getMonth() &&
               date1.getFullYear() === date2.getFullYear();
    };

    // Helper function to check if we need to show a date separator
    const needsDateSeparator = (currentIndex: number): boolean => {
        if (currentIndex === 0) return true; // Always show date separator for the first prompt
        
        const currentPrompt = prompts[currentIndex];
        const previousPrompt = prompts[currentIndex - 1];
        
        if (!currentPrompt.queryOn || !previousPrompt.queryOn) return false;
        
        return !isSameDay(new Date(currentPrompt.queryOn), new Date(previousPrompt.queryOn));
    };

    return (
        <div className='promptList' ref={promptList} style={{ opacity: isReady ? 1 : 0, transition: 'opacity 160ms ease' }}>
            {prompts.map((prompt, index) => (
                <div key={`prompt_container_${prompt.id}`}>
                    {needsDateSeparator(index) && prompt.queryOn && (
                        <DateSeparator 
                            key={`date_separator_${index}`}
                            date={new Date(prompt.queryOn)} 
                        />
                    )}
                    <PromptView
                        key={`prompt_${prompt.id}`}
                        prompt={prompt}
                        isLastPrompt={index === prompts.length - 1}
                        onEditQuery={(query) => {
                            onUpdateAnsweredQuery(index, query);
                        }}
                        onUpdate={(actionType, newPrompt) => {
                            const newPrompts = [...prompts];
                            newPrompts[index] = newPrompt;
                            onUpdate(actionType, newPrompts, index);
                        }}
                        onSourceClick={onSourceClick}
                        onEditingChange={onEditingChange}
                    />
                </div>
            ))}
            {/* sentinel element used to scroll to bottom */}
            <div ref={endRef} aria-hidden style={{ height: 1, width: '100%' }} />
        </div>
    );
};
